import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    FileText,
    Scale,
    HelpCircle,
    Newspaper,
    Image as ImageIcon,
    Phone,
    Inbox,
    UserCog,
    LogOut,
    Menu,
    X,
} from "lucide-react";
import { LogoMark } from "../common/BrandVisuals";
import { useAdminAuth } from "../../context/AdminAuthProvider";
import { adminApi } from "../../lib/adminApi";

const NAV = [
    { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true },
    { to: "/admin/contenus", label: "Contenus du site", icon: FileText },
    { to: "/admin/domaines", label: "Domaines d'intervention", icon: Scale },
    { to: "/admin/faq", label: "FAQ", icon: HelpCircle },
    { to: "/admin/blog", label: "Blog", icon: Newspaper },
    { to: "/admin/images", label: "Images", icon: ImageIcon },
    { to: "/admin/coordonnees", label: "Coordonnées", icon: Phone },
    { to: "/admin/messages", label: "Boîte de réception", icon: Inbox, unreadKey: "messages" },
    { to: "/admin/compte", label: "Compte", icon: UserCog },
];

export function AdminLayout() {
    const { admin, logout } = useAdminAuth();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [unread, setUnread] = useState(0);

    useEffect(() => {
        // Load unread count on mount, on a 30s poll AND whenever a message
        // action (mark-read/delete/refresh) dispatches the custom event.
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await adminApi.get(
                    "/admin/messages?unread_only=true",
                );
                if (!cancelled) setUnread((data || []).length);
            } catch {
                /* ignore */
            }
        };
        load();
        const t = setInterval(load, 30000);
        const onUpdated = () => load();
        window.addEventListener("admin:messages-updated", onUpdated);
        return () => {
            cancelled = true;
            clearInterval(t);
            window.removeEventListener("admin:messages-updated", onUpdated);
        };
    }, []);

    const doLogout = () => {
        logout();
        nav("/admin/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-ink text-neutral-100">
            {/* Top bar mobile */}
            <header className="lg:hidden fixed top-0 inset-x-0 z-40 bg-ink-deep border-b border-ink-border h-16 flex items-center justify-between px-4">
                <LogoMark size="sm" />
                <button
                    className="text-neutral-200 p-2"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="admin-mobile-menu-btn"
                    aria-label={open ? "Fermer" : "Menu"}
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </header>

            {/* Sidebar */}
            <aside
                data-testid="admin-sidebar"
                className={`fixed top-0 left-0 z-40 w-72 h-screen bg-ink-deep border-r border-ink-border flex flex-col transition-transform duration-200 ${
                    open ? "translate-x-0" : "-translate-x-full"
                } lg:translate-x-0`}
            >
                <div className="px-6 py-6 border-b border-ink-border">
                    <LogoMark size="sm" />
                    <div className="mt-3 text-[0.65rem] tracking-widest uppercase text-gold">
                        Espace administration
                    </div>
                    <div className="mt-1 text-xs text-neutral-500 truncate">
                        {admin?.email}
                    </div>
                </div>
                <nav className="flex-1 overflow-y-auto py-4">
                    {NAV.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                onClick={() => setOpen(false)}
                                data-testid={`admin-nav-${item.to.split("/").pop() || "dashboard"}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-6 py-3 text-sm border-l-2 transition-colors ${
                                        isActive
                                            ? "text-gold border-gold bg-ink"
                                            : "text-neutral-300 border-transparent hover:text-gold hover:bg-ink-surface/50"
                                    }`
                                }
                            >
                                <Icon size={18} strokeWidth={1.5} />
                                <span className="flex-1">{item.label}</span>
                                {item.unreadKey === "messages" && unread > 0 && (
                                    <span
                                        className="text-[0.65rem] font-medium bg-gold text-black px-1.5 py-0.5 rounded-sm"
                                        data-testid="admin-unread-badge"
                                    >
                                        {unread}
                                    </span>
                                )}
                            </NavLink>
                        );
                    })}
                </nav>
                <button
                    onClick={doLogout}
                    data-testid="admin-logout-btn"
                    className="m-4 px-4 py-3 text-sm text-neutral-300 hover:text-red-400 flex items-center gap-2 border border-ink-border hover:border-red-400 transition-colors"
                >
                    <LogOut size={16} /> Déconnexion
                </button>
            </aside>

            {/* Content area */}
            <main
                data-testid="admin-content"
                className="lg:ml-72 pt-16 lg:pt-0 min-h-screen"
            >
                <div className="mx-auto max-w-5xl px-5 md:px-10 py-10">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
